import React, { useState, useEffect, useCallback } from 'react';
import { orderBy } from 'lodash';
import { connect } from 'react-redux';
import { isActive } from '../../../../utils/schedules';
import { Button, Form, Row, Table, Col, Alert } from '../../../ui-kit';
import { IconAngleRight, IconAngleLeft, IconFile } from '../../../ui-kit/icons';
import moment from 'moment';
import {
  operations as contractStatsOperations,
  selectors as contractStatsSelectors
} from '../../../../state/modules/contract-statistics';
import { selectors as groupSelectors } from '../../../../state/modules/groups';
import { selectors as availabilityCodeSelectors } from '../../../../state/modules/availability-codes';
import {
  operations as contractWorkloadBreakdownOperations,
  selectors as contractWorkloadBreakdownSelectors
} from '../../../../state/modules/contract-workload-breakdown';
import { operations as contractOperations, selectors as contractSelectors } from '../../../../state/modules/contracts';
import { getCurrentUser } from '../../../../state/modules/current-user/operations';
import { contractValueFormatter } from '../../../../utils/time';
import { encodeQueryParams } from '../../../../utils/query-params';
import './ContractCompliance.scss';
import Loader from '../../../../components/general/Loader';
import { formatDate } from '../../../../utils/time';
import ContractStatisticsProgress from '../../../../components/contracts/ContractStatisticsProgress';
import ContractConstraintTable from '../../../../components/contracts/ContractConstraintTable';
import ContractWorkloadBreakdownTable from '../../../../components/contracts/ContractWorkloadBreakdownTable';
import NewOvertimeCarryOverForm from './NewOvertimeCarryOverForm';
import { Badge } from '../../../ui-kit';
import { unpackFromLocalStorage, addToLocalStorage } from '../../../../utils/local-storage';

const ContractStatisticsTable = ({ contract, timeUnit }) => {
  return (
    <>
      <Table className="progress-table">
        <tbody>
          <tr>
            <td colSpan={contract.time_unit === 'hour' ? '2' : '3'} />
            {contract.time_unit === 'hour' ? (
              <th style={{ verticalAlign: 'middle' }}>
                {I18n.t('activerecord.attributes.reporting/contracts/contract.balance')}
              </th>
            ) : null}
            <th className="text-end text-nowrap">
              {I18n.t('activerecord.attributes.reporting/contracts/contract.total_workload')}
            </th>
            <th />
            <th className="text-start" style={{ verticalAlign: 'middle' }}>
              {I18n.t('activerecord.attributes.reporting/contracts/contract_type.required_workload')}
            </th>
          </tr>
          <tr>
            <th>{I18n.t('available')}</th>
            <td className="progress-bar-column">
              <ContractStatisticsProgress contractStatistics={contract} showAllocatedOnly={false} />
            </td>
            <th>{contract.time_unit === 'hour' ? contractValueFormatter(contract.balance, timeUnit) : null}</th>
            <th className="text-end">{contractValueFormatter(contract.allocated_workload, timeUnit)}</th>
            <th> / </th>
            <th>{contractValueFormatter(contract.target_workload, timeUnit)}</th>
          </tr>
        </tbody>
      </Table>
      <ContractConstraintTable constraintEvaluation={contract.constraint_evaluation} />
    </>
  );
};

const PeriodCarouselControls = ({ moveUp, moveDown }) => (
  <>
    <Button onClick={moveDown} variant="primary" style={{ float: 'left' }}>
      <IconAngleLeft />
    </Button>
    <Button onClick={moveUp} variant="primary" style={{ float: 'right' }}>
      <IconAngleRight />
    </Button>
  </>
);

const PeriodStatistics = ({ stats, timestamp, selected, contract, onClick }) => {
  const showLoader = () => {
    const contractStartTime = contract?.start_time ? moment(contract.start_time) : null;
    const contractEndTime = contract?.end_time ? moment(contract.end_time) : null;

    // If there is no contract start time, it means the contract has not been loaded yet
    // show the Loader
    if (!contractStartTime) return true;
    // If there is no contract end time, only the lower boundary should be checked
    if (!contractEndTime) return timestamp.isSameOrAfter(contractStartTime);

    return timestamp.isSameOrAfter(contractStartTime) && timestamp.isSameOrBefore(contractEndTime);
  };

  if (!stats)
    return <div className="period-stats">{showLoader() ? <Loader /> : <div>{I18n.t('no_results_found')}</div>}</div>;

  const currentPeriod = moment().isBetween(stats.start_time, stats.end_time);

  return (
    <div className={`period-stats ${selected && 'selected'}`} onClick={onClick}>
      <p>
        <b>{stats.period_name}</b> {currentPeriod && <Badge variant="info">{I18n.t('now')}</Badge>}
      </p>
      <p>
        {moment(stats.start_time).format('MMM D')} - {moment(stats.end_time).format('MMM D')}
      </p>
      <p>
        <b>
          {/* scheduled workload against target workload without leave */}
          {contractValueFormatter(stats.allocated_workload, stats.time_unit)} /{' '}
          {contractValueFormatter(stats.target_workload, stats.time_unit)}
        </b>
      </p>
    </div>
  );
};

const PeriodCarousel = ({ carouselContractStats, timestamps, moveUp, moveDown, setCurrentTime, contract }) => (
  <>
    <PeriodCarouselControls moveDown={moveDown} moveUp={moveUp} />
    <div className="period-carousel">
      {timestamps.map((timestamp, index) => (
        <PeriodStatistics
          stats={carouselContractStats[timestamp.format()]}
          timestamp={timestamp}
          key={timestamp.format()}
          selected={index === 2}
          contract={contract}
          onClick={() => setCurrentTime(moment(timestamp))}
        />
      ))}
    </div>
  </>
);

const ContractCompliance = props => {
  const generateTimestamps = useCallback(
    time => {
      return [
        moment(time).add(-2, props.carouselPeriodType),
        moment(time).add(-1, props.carouselPeriodType),
        moment(time),
        moment(time).add(1, props.carouselPeriodType),
        moment(time).add(2, props.carouselPeriodType)
      ];
    },
    [props.carouselPeriodType]
  );

  const [currentPeriod, setCurrentPeriod] = useState({
    time: moment(props.time).format(),
    timestamps: generateTimestamps(props.weekTime || props.time)
  });

  const userId = props.userId || props.currentUser.id;

  const preferredContractType = unpackFromLocalStorage('contract_type_id');

  const [displayedContractId, setDisplayedContractId] = useState(null);
  const [showCarryOverForm, setShowCarryOverForm] = useState(false);

  const loadTableStats = useCallback(() => {
    if (!userId) return;

    // Load yearly stats (if there are no stats for the current period)
    if (!filterContractStatistics(props.tablePeriodType, currentPeriod.time)) {
      props.fetchContractStatistics({
        ids: userId,
        params: {
          time_unit: props.timeUnit,
          time: moment(currentPeriod.time).format(),
          end_time: props.endTime,
          period_type: props.tablePeriodType
        }
      });
    }
  }, [userId, props.timeUnit, props.tablePeriodType, currentPeriod]);

  const loadCarouselStats = useCallback(
    (force = false) => {
      if (!userId) return;

      // Load weekly stats
      for (const time of currentPeriod.timestamps) {
        if (!force && filterContractStatistics(props.carouselPeriodType, time)) continue;

        props.fetchContractStatistics({
          ids: userId,
          params: {
            time_unit: props.timeUnit,
            time: moment(time).format(),
            period_type: props.carouselPeriodType
          }
        });
      }
    },
    [userId, props.timeUnit, props.carouselPeriodType, currentPeriod.timestamps]
  );

  const loadCurrentWeekWorkloadBreakdown = useCallback(() => {
    if (!displayedContractId) return;
    if (!props.showBreakdown) return;

    props.fetchContractWorkloadBreakdown({
      ids: displayedContractId,
      params: {
        time_unit: props.timeUnit,
        time: moment(currentPeriod.time).format(),
        period_type: props.carouselPeriodType
      }
    });
  }, [props.timeUnit, props.carouselPeriodType, currentPeriod.time, displayedContractId]);

  const loadAllStats = useCallback(() => {
    loadTableStats();
    loadCarouselStats(true);
  }, []);

  const moveTimeUp = () => {
    setCurrentTime(moment(currentPeriod.time).add(1, props.carouselPeriodType));
  };

  const moveTimeDown = () => {
    setCurrentTime(moment(currentPeriod.time).subtract(1, props.carouselPeriodType));
  };

  const setCurrentTime = time => {
    setCurrentPeriod({
      time: moment(time),
      timestamps: generateTimestamps(time)
    });
  };

  const getContractWorkloadBreakdown = () =>
    filterContractWorkloadBreakdown(props.carouselPeriodType, currentPeriod.time);

  const getCarouselContractStats = () => {
    let result = {};

    for (const timestamp of currentPeriod.timestamps)
      result[timestamp.format()] = filterContractStatistics(props.carouselPeriodType, timestamp);

    return result;
  };

  const filterContractStatistics = (periodType, time) =>
    props.contractStatisticsFilter(props.timeUnit, periodType, time, displayedContractId)[0];

  const filterContractWorkloadBreakdown = (periodType, time) =>
    props.contractWorkloadBreakdownFilter(props.timeUnit, periodType, time, displayedContractId)[0];

  const toggleShowCarryOverForm = useCallback(value => setShowCarryOverForm(value), []);

  useEffect(() => {
    props.getCurrentUser();
    props.dashboardAPIManager.fetchAvailabilityCodes({ params: { presentation_type: 'basic' } });
    props.fetchContracts({ params: { user_ids: props.userId } });
  }, []);

  useEffect(() => {
    const newContract = props.contractsById[displayedContractId];
    if (!newContract) return;

    const contractType = newContract.contract_type.id;
    addToLocalStorage('contract_type_id', contractType);
  }, [displayedContractId]);

  const userContracts = Object.values(props.contractsById).filter(c => c.user_id === userId);

  useEffect(() => {
    // if there is no contract set, then set the first active contract
    // as the displayed one. If there are no active contracts, use the first
    // contract
    if (displayedContractId !== null || userContracts.length === 0) return;

    let contracts = orderBy(userContracts, ['start_time', 'desc']);

    // get the contracts with the preferred contract type
    const preferredContracts = contracts.filter(c => c.contract_type.id === preferredContractType);
    // if there are any, these will be preferred as the initial selection
    contracts = preferredContracts.length > 0 ? preferredContracts : contracts;

    const activeContract = contracts.find(contract => isActive(contract.start_time, contract.end_time));
    const selectedContract = activeContract || contracts[0];
    setDisplayedContractId(selectedContract.id);
  }, [userId, props.contractsById]);

  useEffect(loadTableStats, [userId, props.timeUnit, props.tablePeriodType, currentPeriod.time]);

  useEffect(loadCurrentWeekWorkloadBreakdown, [
    userId,
    props.timeUnit,
    props.tablePeriodType,
    currentPeriod.time,
    displayedContractId
  ]);

  useEffect(loadCarouselStats, [userId, props.timeUnit, props.carouselPeriodType, currentPeriod.timestamps]);

  if (!props.initialLoadComplete) return <Loader />;

  if (props.contractsLoaded && userContracts.length === 0)
    return <Alert variant="warning">{I18n.t('no_results_found')}</Alert>;

  const carouselContractStats = getCarouselContractStats();

  const contract = props.contractsById[displayedContractId];

  // Link to the contracts breakdown for the specified contract
  const params = encodeQueryParams({
    period_type: props.tablePeriodType,
    start_time: currentPeriod.time,
    time_unit: props.timeUnit
  });
  const detailsUrl = `/users/${userId}/reporting/contracts/contracts/${displayedContractId}?${params}`;

  // Get the contract stats for the table with the requested period type
  const contractTableStats = filterContractStatistics(
    props.tablePeriodType,
    props.tablePeriodType === 'custom' ? props.endTime : currentPeriod.time
  );

  return (
    <div className="contract-compliance">
      {contract && (
        <NewOvertimeCarryOverForm
          showCarryOverForm={showCarryOverForm}
          setShowCarryOverForm={toggleShowCarryOverForm}
          contract={contract}
          time={props.time}
          loadContractStatistics={loadAllStats}
          periodType={props.tablePeriodType}
        />
      )}
      <Form>
        <Form.Group as={Row} controlId="contractSelect" className="w-100">
          <Form.Label column sm={2}>
            {I18n.t('activerecord.models.reporting/contracts/contract')}
          </Form.Label>
          <Col sm={6}>
            <Form.Select
              value={displayedContractId || ''}
              onChange={e => {
                setDisplayedContractId(e.target.value);
              }}
            >
              {userContracts.map(contract => (
                <option key={contract.id} value={contract.id || ''}>
                  {contract.contract_type.name}{' '}
                  {moment(contract.end_time).isBefore(moment()) &&
                    `(${I18n.t('reporting.contracts.contract_types.expired')})`}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col sm={2}>
            <Button href={detailsUrl} target="_blank">
              {I18n.t('details')}
            </Button>
          </Col>
          {props.contractsLoaded &&
            props.createCarryOverRights &&
            contract?.contract_type?.period === props.tablePeriodType && (
              <Col sm={2}>
                <Button onClick={() => setShowCarryOverForm(true)} variant="primary">
                  <IconFile />
                  {` ${I18n.t('activerecord.models.reporting/contracts/overtime_carry_over')}`}
                </Button>
              </Col>
            )}
        </Form.Group>
      </Form>
      <br />
      {!!contractTableStats || <Loader />}
      {contractTableStats && (
        <>
          <h4 className="text-center">
            {contractTableStats.period_name}{' '}
            <small>
              ({formatDate(contractTableStats.start_time)} - {formatDate(contractTableStats.end_time)})
            </small>{' '}
            <Badge variant="info">{I18n.t(`classy_enum.time_unit.${props.timeUnit}`)}</Badge>
          </h4>
          <h5 className="me-auto" />
          <ContractStatisticsTable contract={contractTableStats} timeUnit={props.timeUnit} />
        </>
      )}
      {props.showCarousel && props.carouselPeriodType !== props.tablePeriodType && (
        <PeriodCarousel
          carouselContractStats={carouselContractStats}
          timestamps={currentPeriod.timestamps}
          moveUp={moveTimeUp}
          moveDown={moveTimeDown}
          setCurrentTime={setCurrentTime}
          contract={contract}
        />
      )}
      <br />
      {props.showBreakdown && (
        <ContractWorkloadBreakdownTable
          workloadBreakdown={getContractWorkloadBreakdown()}
          availabilityCodesById={props.availabilityCodesById}
          fetchGroups={props.dashboardAPIManager.fetchGroups}
          groupsById={props.groupsById}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  contractStatisticsFilter: contractStatsSelectors.selectContractStatisticsFilter(state),
  contractWorkloadBreakdownFilter: contractWorkloadBreakdownSelectors.selectContractWorkloadBreakdownFilter(state),
  initialLoadComplete: contractStatsSelectors.initialLoadComplete(state),
  contractsById: contractSelectors.byId(state),
  contractsLoaded: contractSelectors.initialLoadComplete(state),
  availabilityCodesById: availabilityCodeSelectors.byId(state),
  groupsById: groupSelectors.byId(state),
  currentUser: state.currentUser.data
});

const mapDispatchToProps = {
  fetchContractStatistics: contractStatsOperations.fetch,
  fetchContractWorkloadBreakdown: contractWorkloadBreakdownOperations.fetch,
  fetchContracts: contractOperations.fetch,
  getCurrentUser
};

export default connect(mapStateToProps, mapDispatchToProps)(ContractCompliance);
